import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import { lookupMarianaTekUser, lookupBarrysPlatformUser } from '../services/api'

const FormContainer = styled.div`
  background: ${theme.color.background.frame};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 16px ${theme.color.shadow.medium}, 
              0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
`

const FormTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`

const Label = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: ${theme.color.accent.primary};
  }
`

const Input = styled.input`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light},
                0 0 0 3px rgba(215, 0, 54, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    color: #666;
    cursor: not-allowed;
  }
`

const Select = styled.select`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light},
                0 0 0 3px rgba(215, 0, 54, 0.1);
  }
`

const ButtonContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  grid-column: 1 / -1;
`

const SuccessMessage = styled.div`
  background: #e6f7e6;
  color: #2d7d2d;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  grid-column: 1 / -1;
`

const InfoMessage = styled.div`
  background: #e3f2fd;
  color: #1976d2;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  grid-column: 1 / -1;
`

export interface CreateProfileFormData {
  firstName: string
  lastName: string
  dateOfBirth: string
  email: string
  gender: string
  address: string
  city: string
  state: string
  country: string
  zip: string
  phone: string
  marianaTekId?: string
  barrysPlatformId?: string
}

interface CreateProfileFormProps {
  initialEmail?: string
  onSubmit: (data: CreateProfileFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  error?: string
  success?: string
}

const CreateProfileForm: React.FC<CreateProfileFormProps> = ({
  initialEmail = '',
  onSubmit,
  onCancel,
  isLoading = false,
  error,
  success
}) => {
  const [formData, setFormData] = useState<CreateProfileFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: initialEmail,
    gender: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    zip: '',
    phone: '',
    marianaTekId: '',
    barrysPlatformId: ''
  })

  const [lookupStatus, setLookupStatus] = useState<{
    marianaTek: 'pending' | 'found' | 'not_found' | 'error'
    barrys: 'pending' | 'found' | 'not_found' | 'error'
  }>({
    marianaTek: 'pending',
    barrys: 'pending'
  })

  useEffect(() => {
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }))
      // Trigger lookups when email is set
      performLookups(initialEmail)
    }
  }, [initialEmail])

  const performLookups = async (email: string) => {
    if (!email) return

    // MarianaTek lookup
    try {
      setLookupStatus(prev => ({ ...prev, marianaTek: 'pending' }))
      const data = await lookupMarianaTekUser(email)
      
      if (data.data && data.data.length > 0) {
        const user = data.data[0]
        setFormData(prev => ({
          ...prev,
          marianaTekId: user.id,
          firstName: user.attributes.first_name || prev.firstName,
          lastName: user.attributes.last_name || prev.lastName,
          dateOfBirth: user.attributes.birth_date || prev.dateOfBirth,
          phone: user.attributes.phone_number || prev.phone,
          address: user.attributes.address_line1 || prev.address,
          city: user.attributes.city || prev.city,
          state: user.attributes.state_province || prev.state,
          zip: user.attributes.postal_code || prev.zip,
          country: user.attributes.country || prev.country,
          gender: user.attributes.gender || prev.gender
        }))
        setLookupStatus(prev => ({ ...prev, marianaTek: 'found' }))
      } else {
        setLookupStatus(prev => ({ ...prev, marianaTek: 'not_found' }))
      }
    } catch (err) {
      setLookupStatus(prev => ({ ...prev, marianaTek: 'error' }))
    }

    // Barry's Platform lookup via PostgreSQL
    try {
      setLookupStatus(prev => ({ ...prev, barrys: 'pending' }))
      const data = await lookupBarrysPlatformUser(email)
      
      if (data.found && data.id) {
        setFormData(prev => ({
          ...prev,
          barrysPlatformId: data.id
        }))
        setLookupStatus(prev => ({ ...prev, barrys: 'found' }))
      } else {
        setLookupStatus(prev => ({ ...prev, barrys: 'not_found' }))
      }
    } catch (err) {
      setLookupStatus(prev => ({ ...prev, barrys: 'error' }))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Trigger lookups when email changes
    if (name === 'email' && value !== initialEmail) {
      performLookups(value)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!formData.email.trim()) {
      return
    }

    await onSubmit(formData)
  }

  const getLookupStatusMessage = () => {
    const messages = []
    
    if (lookupStatus.marianaTek === 'found') {
      messages.push(`✓ MarianaTek user found (ID: ${formData.marianaTekId}) - Some fields auto-populated`)
    } else if (lookupStatus.marianaTek === 'not_found') {
      messages.push('⚠ MarianaTek user not found - Please fill all fields manually')
    } else if (lookupStatus.marianaTek === 'error') {
      messages.push('❌ Error looking up MarianaTek user')
    } else {
      messages.push('🔍 Looking up MarianaTek user...')
    }

    if (lookupStatus.barrys === 'found') {
      messages.push(`✓ Barry's Platform user found (ID: ${formData.barrysPlatformId})`)
    } else if (lookupStatus.barrys === 'not_found') {
      messages.push('⚠ Barry\'s Platform user not found - Will create new profile')
    } else if (lookupStatus.barrys === 'error') {
      messages.push('❌ Error looking up Barry\'s Platform user')
    } else {
      messages.push('🔍 Looking up Barry\'s Platform user...')
    }

    return messages
  }

  return (
    <FormContainer>
      <FormTitle>Create User Profile</FormTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      <InfoMessage>
        {getLookupStatusMessage().map((message, index) => (
          <div key={index}>{message}</div>
        ))}
      </InfoMessage>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <RequiredLabel htmlFor="firstName">First Name</RequiredLabel>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <RequiredLabel htmlFor="lastName">Last Name</RequiredLabel>
          <Input
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="dateOfBirth">Date of Birth</Label>
          <Input
            id="dateOfBirth"
            name="dateOfBirth"
            type="date"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
          />
        </FormGroup>

        <FormGroupFull>
          <RequiredLabel htmlFor="email">Email Address</RequiredLabel>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </FormGroupFull>

        <FormGroup>
          <Label htmlFor="gender">Gender</Label>
          <Select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="non-binary">Non-binary</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
          />
        </FormGroup>

        <FormGroupFull>
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            name="address"
            type="text"
            value={formData.address}
            onChange={handleInputChange}
          />
        </FormGroupFull>

        <FormGroup>
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleInputChange}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="state">State/Province</Label>
          <Input
            id="state"
            name="state"
            type="text"
            value={formData.state}
            onChange={handleInputChange}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="country">Country</Label>
          <Select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IT">Italy</option>
            <option value="ES">Spain</option>
            <option value="NL">Netherlands</option>
            <option value="other">Other</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="zip">ZIP/Postal Code</Label>
          <Input
            id="zip"
            name="zip"
            type="text"
            value={formData.zip}
            onChange={handleInputChange}
          />
        </FormGroup>

        {formData.marianaTekId && (
          <FormGroup>
            <Label htmlFor="marianaTekId">MarianaTek ID</Label>
            <Input
              id="marianaTekId"
              name="marianaTekId"
              type="text"
              value={formData.marianaTekId}
              disabled
            />
          </FormGroup>
        )}

        {formData.barrysPlatformId && (
          <FormGroup>
            <Label htmlFor="barrysPlatformId">Barry's Platform ID</Label>
            <Input
              id="barrysPlatformId"
              name="barrysPlatformId"
              type="text"
              value={formData.barrysPlatformId}
              disabled
            />
          </FormGroup>
        )}

        <ButtonContainer>
          <Button type="button" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" primary disabled={isLoading || !formData.email.trim()}>
            {isLoading ? 'Creating Profile...' : 'Create Profile'}
          </Button>
        </ButtonContainer>
      </Form>
    </FormContainer>
  )
}

export default CreateProfileForm